/**
 * DancerMediaService
 *
 * Service for managing dancer media uploads (headshots, photos, videos) for profile wizard
 * Follows ReimbursementDocumentService pattern
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { isTransientNetworkError } from '@kit/shared/lib/is-transient-network-error';
import { captureServiceError } from '@kit/shared/lib/sentry-helpers';
import { Result, ServiceError } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import {
  createR2ServiceFromEnv,
  createStorageUrlService,
  SignedUrlExpiry,
  StorageBuckets,
  type R2Service,
  type StorageUrlService,
} from '@kit/shared/storage';
import {
  createMuxVideoServiceFromEnv,
  type VideoService,
} from '@kit/shared/video';
import type { Database } from '@kit/supabase/database';

import { looksLikePdf, PDF_SNIFF_BYTES } from '../lib/pdf-signature';
import { deleteMedia } from './dancer-media-delete';
import {
  fetchStorageObjectHash,
  findExistingUploadedMedia,
  readStorageHead,
  showWhenUploadedShown,
} from './dancer-media-finalize-support';
import {
  getHeadshot,
  getMediaByUser,
  removeDeferredPublicCopies,
  reorder,
  setShownOnProfile,
  updateMedia,
} from './dancer-media-library';
import { getSignedUrl, getSignedUrls } from './dancer-media-signed-urls';
import { uploadMedia } from './dancer-media-upload';
import { addVideoLink, ingestNativeVideoToMux } from './dancer-media-video';
import {
  FINALIZE_MAX_ATTEMPTS,
  FINALIZE_RETRY_BASE_MS,
  sleep,
} from './dancer-media.constants';
import type { DancerMediaContext } from './dancer-media.context';
import type {
  DancerMedia,
  FinalizeDirectUploadInput,
  MediaUploadInput,
  VideoLinkInput,
} from './dancer-media.schema';

export type {
  CropRectInput,
  DancerMedia,
  FinalizeDirectUploadInput,
  MediaUploadInput,
  VideoLinkInput,
} from './dancer-media.schema';
export {
  CROP_ASPECT,
  CROP_ASPECT_TOLERANCE,
  validateCropRect,
} from './dancer-media.schema';

/**
 * Service for managing dancer media uploads
 */
export class DancerMediaService extends BaseService {
  private storageService: StorageUrlService;
  private readonly videoServiceOverride: VideoService | null;
  private readonly r2ServiceOverride: R2Service | null;

  /**
   * @param deps Optional injected collaborators. In production these are left
   * undefined and resolved lazily from env on first native-video ingest, so
   * the (far more common) photo/headshot path never touches the Mux/R2 env.
   * Tests inject mocks to exercise `finalizeDirectUpload` without live vendors.
   */
  constructor(
    client: SupabaseClient<Database>,
    deps?: { videoService?: VideoService; r2Service?: R2Service },
  ) {
    super(client);
    this.storageService = createStorageUrlService(client);
    this.videoServiceOverride = deps?.videoService ?? null;
    this.r2ServiceOverride = deps?.r2Service ?? null;
  }

  private get ctx(): DancerMediaContext {
    return {
      client: this.client,
      log: (level, message, context) => this.log(level, message, context),
      storageService: this.storageService,
      getVideoService: () => this.getVideoService(),
      getR2Service: () => this.getR2Service(),
    };
  }

  /** Lazily resolve the video-delivery vendor (Mux behind the Rule-11 boundary). */
  private getVideoService(): VideoService {
    return this.videoServiceOverride ?? createMuxVideoServiceFromEnv();
  }

  /** Lazily resolve the R2 origin store (the single R2 edit surface, Rule 11). */
  private getR2Service(): R2Service {
    return this.r2ServiceOverride ?? createR2ServiceFromEnv();
  }

  /**
   * Upload media file to storage and create database record
   * Upload a media file (headshot, photo, or video) to dancer-media storage
   * and create the matching artist_media row. For external video URLs
   * (YouTube/Vimeo), use addVideoLink() instead.
   */
  async uploadMedia(
    userId: string,
    file: File,
    input: MediaUploadInput,
  ): Promise<Result<DancerMedia>> {
    return uploadMedia(this.ctx, userId, file, input);
  }

  /**
   * Finalize a browser direct-upload (signed-URL flow). The file is already
   * in storage at `input.storagePath`; this inserts the canonical
   * artist_media row, applies the headshot profile update, and (for videos)
   * returns a signed URL for immediate inline playback.
   *
   * Single edit surface for artist_media inserts: the setup-wizard finalize
   * action previously inlined its own insert payload, which drifted from
   * uploadMedia's (no profiles.profile_photo_url stamp for headshots; the
   * service path lacked professional_profile_id). Both paths now build the
   * row here.
   */
  async finalizeDirectUpload(
    userId: string,
    input: FinalizeDirectUploadInput,
  ): Promise<Result<DancerMedia & { signed_url: string | null }>> {
    const logContext = {
      method: 'finalizeDirectUpload',
      userId,
      mediaType: input.mediaType,
      storagePath: input.storagePath,
    };

    try {
      // Path must start with the caller's own id — the same boundary the
      // storage RLS uses, so a malicious caller can't claim someone else's
      // just-uploaded object. The RPC re-checks it for the row; this early
      // copy guards the storage reads/removals below, which run first.
      if (!input.storagePath.startsWith(`${userId}/`)) {
        this.log('warn', 'Storage path outside caller boundary', logContext);
        return Result.fail(ServiceError.validation('Invalid storage path'));
      }

      // Orphan-row prevention (R2, BAL-595): the bytes were PUT to the signed
      // URL by the browser out-of-band. Verify the object actually landed
      // before inserting the row — a failed/aborted PUT must NOT leave a row
      // pointing at a non-existent object. The same info() read also yields
      // the object's ETag, from which we derive content_hash (BAL-700) — one
      // round-trip does both, no extra storage call.
      // Re-check with bounded backoff: the browser PUT and this server-side
      // `.info()` read hit Supabase Storage over two independent connections,
      // so under concurrent load the object metadata can lag behind a PUT the
      // client already saw succeed (read-after-write). A single miss must not
      // strand the just-uploaded file (BALLEE-G7 — 87 orphan objects during the
      // GDO apply burst 2026-07-09) — fetchStorageObjectHash retries internally.
      const objectInfo = await this.fetchStorageObjectHash(input.storagePath);
      if (!objectInfo.exists) {
        this.log('warn', 'Finalize aborted: uploaded object not found', {
          ...logContext,
        });
        return Result.fail(
          ServiceError.notFound(
            'Uploaded file was not found in storage. Please retry the upload.',
            { reason: 'object_not_found_after_retry' },
          ),
        );
      }
      const contentHash = objectInfo.contentHash;

      // Idempotent re-upload guard (BAL-700): mirrors the uploadMedia guard
      // above. The browser already PUT the bytes to input.storagePath before
      // this call landed; if a live row for this exact
      // (user_id, media_type, file_name) — OR the same bytes (content_hash)
      // under a different name — already exists, remove the just-uploaded
      // duplicate object (best-effort — no row will ever reference it) and
      // return the existing row instead of inserting a second one. Bounded to
      // at most two lookups, no N+1.
      const reuseExisting = input.reuseExisting ?? true;
      let existingMedia = await this.findExistingUploadedMedia(
        userId,
        input.mediaType,
        input.fileName,
        contentHash,
        // The row already at this path is this upload, finalized before (a
        // retry after a lost response). It is not a duplicate: the RPC below
        // upserts on (user_id, storage_path) and returns it, while the
        // duplicate branches would remove its own file.
        { matchName: reuseExisting, excludeStoragePath: input.storagePath },
      );
      if (existingMedia && !reuseExisting) {
        // An upload on someone's behalf never resolves to an existing row:
        // returning it would hand the uploader that item and its URL. Only
        // identical bytes reach here (the name is not matched), so the refusal
        // tells the uploader nothing they did not already hold.
        this.log('info', 'Duplicate upload refused: existing row not reused', {
          ...logContext,
          existingMediaId: existingMedia.id,
        });
        try {
          await this.client.storage
            .from(StorageBuckets.DANCER_MEDIA)
            .remove([input.storagePath]);
        } catch {
          // ignore — cron cleanup sweeps stale objects eventually
        }
        return Result.fail(
          ServiceError.conflict("This file is already in the user's library", {
            reason: 'duplicate_not_reused',
          }),
        );
      }
      // Reusing a private row for a shown upload makes that row public, so it
      // needs proof that the row IS this file: the same bytes. A name match
      // with an unknown hash on either side is no proof, and the upload is
      // saved as a new item instead.
      if (
        existingMedia &&
        input.shownOnProfile !== false &&
        existingMedia.shown_on_profile === false &&
        !(contentHash && existingMedia.content_hash === contentHash)
      ) {
        this.log('info', 'Name match on a private item not reused', {
          ...logContext,
          existingMediaId: existingMedia.id,
        });
        existingMedia = null;
      }
      if (existingMedia) {
        this.log(
          'info',
          'Duplicate direct upload detected — returning existing row',
          { ...logContext, existingMediaId: existingMedia.id },
        );
        try {
          await this.client.storage
            .from(StorageBuckets.DANCER_MEDIA)
            .remove([input.storagePath]);
        } catch {
          // ignore — cron cleanup sweeps stale objects eventually
        }
        const existingRow = await this.showWhenUploadedShown(
          existingMedia,
          input.shownOnProfile,
          logContext,
        );
        let existingSignedUrl: string | null = null;
        if (existingRow.storage_path) {
          const existingSignedUrlResult = await this.getSignedUrl(
            existingRow.storage_path,
          );
          if (existingSignedUrlResult.isSuccess) {
            existingSignedUrl = existingSignedUrlResult.getValue();
          }
        }
        return Result.ok({ ...existingRow, signed_url: existingSignedUrl });
      }

      // Content check for documents (BAL-747). Step 1 of this flow validates
      // only client-supplied metadata — it runs before the browser has PUT
      // anything — and it now accepts a generic `application/octet-stream` for a
      // `.pdf` name, because some browsers report exactly that for a valid PDF.
      // This is where that relaxation is paid for: the bytes now exist, so the
      // %PDF signature is checked for real. Without it the allowlist change
      // would be cosmetic and any file could enter the document bucket.
      //
      // Deliberately AFTER the dedup guard: a re-upload of bytes we already have
      // is answered from the existing row without paying for a second storage
      // read, and stays idempotent even if that read would have blipped.
      let mimeType = input.mimeType;
      if (input.mediaType === 'document') {
        const head = await this.readStorageHead(
          input.storagePath,
          PDF_SNIFF_BYTES,
        );

        if (!head) {
          // Could not read the object back, even after retries. Distinct from
          // "not a PDF": the file may well be fine, so the object is LEFT IN
          // PLACE rather than deleted — destroying a legitimate CV over a
          // transient storage fault is the worse failure. Re-uploading mints a
          // fresh path, so this object becomes an orphan for the cron sweep;
          // that is the accepted cost of not deleting on an unknown.
          this.log('warn', 'Finalize aborted: document signature unreadable', {
            ...logContext,
          });
          return Result.fail(
            new ServiceError(
              'INTERNAL_ERROR',
              'Could not verify the uploaded document. Please retry the upload.',
              undefined,
              { reason: 'document_signature_unreadable' },
            ),
          );
        }

        // An empty read is a verdict, not an unknown: the stream ended cleanly
        // with no bytes, so whatever landed is not a PDF. It falls through to
        // the rejection below rather than the retry-forever branch above.
        if (!looksLikePdf(head)) {
          // Definitively not a PDF. Remove the object we just accepted so a
          // rejected upload never lingers in the bucket (same orphan-cleanup
          // posture as the duplicate branch above).
          this.log('warn', 'Finalize rejected: document is not a PDF', {
            ...logContext,
            declaredMimeType: input.mimeType,
            headBytes: head.length,
          });
          try {
            await this.client.storage
              .from(StorageBuckets.DANCER_MEDIA)
              .remove([input.storagePath]);
          } catch {
            // Best-effort — cron cleanup sweeps stale objects eventually.
          }
          return Result.fail(
            ServiceError.validation(
              'This file is not a valid PDF. Please upload a PDF document.',
            ),
          );
        }

        // The signature confirmed what the browser could not label. Persist the
        // real type, so the readers that branch on `mime_type === 'application/pdf'`
        // (classifyMediaKind, the media thumbnail, the media picker's pdf alias)
        // treat an octet-stream-reported CV as the document it is.
        mimeType = 'application/pdf';
      }

      // The row contract lives once, in the finalize_dancer_media_upload RPC
      // (BAL-603, Rule 8), shared with the mobile app: column set,
      // professional_profile_id link, is_primary, migrated_from, the 4:5 crop
      // (validated there; an invalid rect is DROPPED, never an error — the
      // public hero letterboxes the full picture), the headshot profile stamp
      // (same transaction as the row), and the Mux 'preparing' stamp.
      const rpcArgs = {
        p_user_id: userId,
        p_media_type: input.mediaType,
        p_storage_path: input.storagePath,
        p_file_name: input.fileName,
        p_file_size_bytes: input.fileSize,
        // Normalised above for documents whose browser-reported type was a
        // generic binary MIME (BAL-747); the client value verbatim otherwise.
        p_mime_type: mimeType,
        p_content_hash: contentHash ?? undefined,
        p_title: input.title ?? undefined,
        p_description: input.description ?? undefined,
        p_category: input.category ?? undefined,
        p_crop: input.crop
          ? {
              x: input.crop.x,
              y: input.crop.y,
              w: input.crop.w,
              h: input.crop.h,
              width: input.crop.width,
              height: input.crop.height,
            }
          : undefined,
        // Native video enters the Mux pipeline immediately (BAL-721 / R4/R5):
        // the RPC stamps 'preparing' AT INSERT, before the ingest byte-copy
        // runs, so a request killed mid-ingest still leaves the row on the
        // mux_status reconciliation index. Set only because this path runs
        // ingestNativeVideoToMux below.
        p_start_mux_ingest: input.mediaType === 'video',
        // The upload surface decides: the profile editor shows the item, an
        // application form keeps it private (default true matches the column).
        p_shown_on_profile: input.shownOnProfile ?? true,
      };

      // Idempotent, bounded-retry write (BAL-696). A transient Supabase
      // timeout can abort the response AFTER Postgres has committed the row;
      // retrying a plain INSERT would then double-insert. The RPC UPSERTs on
      // the (user_id, storage_path) idempotency key so a retry returns the
      // same row instead of a duplicate, and we retry only transient/timeout
      // failures a few times so a brief blip self-heals server-side rather
      // than bouncing to the user (the BAL-127 stopgap did the latter).
      let media: Database['public']['Tables']['artist_media']['Row'] | null =
        null;
      let dbError: { message: string; code?: string } | null = null;

      for (let attempt = 1; attempt <= FINALIZE_MAX_ATTEMPTS; attempt++) {
        const { data, error } = await this.client.rpc(
          'finalize_dancer_media_upload',
          rpcArgs,
        );

        media = data;
        dbError = error;

        if (!error && data) break;

        const transient = isTransientNetworkError(error);
        if (transient && attempt < FINALIZE_MAX_ATTEMPTS) {
          this.log('warn', 'Transient finalize write failure — retrying', {
            ...logContext,
            attempt,
            error: error?.message,
          });
          await sleep(FINALIZE_RETRY_BASE_MS * attempt);
          continue;
        }
        break;
      }

      if (dbError || !media) {
        const transient = isTransientNetworkError(dbError);
        // Non-transient DB rejection → clean rollback: the insert was refused,
        // so the just-uploaded object is a genuine orphan; remove it.
        // Transient exhaustion → DO NOT delete: the row may have committed on a
        // response that timed out, so the object might still own a live row.
        // The idempotent upsert on the user's next retry (or the BAL-657
        // reconciliation cron) reconciles it; deleting here could strand a
        // committed row pointing at a now-missing object.
        if (!transient) {
          try {
            await this.client.storage
              .from(StorageBuckets.DANCER_MEDIA)
              .remove([input.storagePath]);
          } catch {
            // ignore — cron cleanup sweeps stale objects eventually
          }
        }
        this.log('error', 'Database write failed for direct upload', {
          ...logContext,
          error: dbError?.message,
          errorCode: dbError?.code,
          transient,
        });
        return Result.fail(
          ServiceError.database(dbError?.message ?? 'Failed to save media'),
        );
      }

      // The RPC keeps the stored visibility on a conflicting re-upload; a shown
      // upload still has to show the item (R4).
      media = await this.showWhenUploadedShown(
        media,
        input.shownOnProfile,
        logContext,
      );

      // Native-video ingest (BAL-721 / R1): a raw .mov/.mp4 dancer upload is not
      // reliably browser-playable, so move the raw object to R2 (the ingest
      // origin, decision D1) and hand its URL to Mux for HLS re-encode. This
      // mutates `media` in place to carry provider/mux_asset_id/mux_status so the
      // finalize response already reflects the 'preparing' state (drives the R4
      // processing UI). Ingest failures are surfaced as mux_status='errored' (R5),
      // never thrown — the upload row + object already exist and must not be lost.
      if (input.mediaType === 'video') {
        await this.ingestNativeVideoToMux(media, userId, logContext);
      }

      // Surface a signed URL for any uploaded file so the wizard can render
      // the thumbnail/preview immediately. dancer-media is a PRIVATE bucket,
      // so the bare storage_path is just a key the browser can't resolve.
      // Previously only videos were signed — a just-uploaded headshot/photo
      // therefore rendered as a placeholder file icon until a page reload
      // picked up the loader's signed URL.
      let signedUrl: string | null = null;
      if (media.storage_path) {
        const signedUrlResult = await this.getSignedUrl(media.storage_path);
        if (signedUrlResult.isSuccess) {
          signedUrl = signedUrlResult.getValue();
        }
      }

      this.log('info', 'Direct upload finalised', {
        ...logContext,
        mediaId: media.id,
      });

      return Result.ok({ ...media, signed_url: signedUrl });
    } catch (error) {
      const serviceError = ServiceError.internal(
        error instanceof Error ? error.message : 'Unknown error',
        error,
      );
      captureServiceError(serviceError, {
        action: 'finalizeDirectUpload',
        step: 'execution',
      });
      return Result.fail(serviceError);
    }
  }

  /**
   * Idempotent-upload guard (BAL-700): look up an existing, live
   * (requires_reupload = false) artist_media row that a new upload would
   * duplicate. Uploaders (uploadMedia, finalizeDirectUpload) call this
   * before inserting so a re-upload of an already-uploaded file returns the
   * existing row instead of creating a duplicate — the only previous
   * uniqueness guard was idx_artist_media_unique_headshot (one headshot per
   * user), which never covered photos, videos, or documents.
   *
   * A row is a duplicate if it matches the same (user_id, media_type) AND
   * either the same file_name OR the same content_hash (identical bytes
   * saved under a different name). A file_name match whose bytes are known
   * to differ (both hashes set and unequal) is a different file, not a
   * duplicate. The content_hash branch also makes the
   * common same-bytes-rename case return gracefully rather than surfacing
   * as a raw unique-violation from idx_artist_media_unique_content_hash.
   *
   * Excludes requires_reupload rows (Meteor lost-file placeholders) — those
   * genuinely need fresh bytes, not a return of the same dead reference — and
   * lost_at rows (retired duplicates), which no reader shows.
   * Bounded to at most two lookups (filename, then hash) — never per-row, so
   * no N+1.
   */
  private async findExistingUploadedMedia(
    userId: string,
    mediaType: string,
    fileName: string,
    contentHash?: string | null,
    options: { matchName?: boolean; excludeStoragePath?: string } = {},
  ): Promise<Database['public']['Tables']['artist_media']['Row'] | null> {
    return findExistingUploadedMedia(
      this.ctx,
      userId,
      mediaType,
      fileName,
      contentHash,
      options,
    );
  }

  /**
   * Fetch a storage object's existence + byte-content hash in one round-trip
   * (BAL-700 / BAL-595). Uses storage.info(), whose response carries the S3
   * ETag; normalizeContentHash turns a single-PUT ETag into the md5 content
   * hash (and returns null for multipart / non-md5 ETags).
   */
  private async fetchStorageObjectHash(
    storagePath: string,
    attempts = 4,
    delayMs = 300,
  ): Promise<{ exists: boolean; contentHash: string | null }> {
    return fetchStorageObjectHash(this.ctx, storagePath, attempts, delayMs);
  }

  /**
   * Read the first `byteCount` bytes of a stored object (BAL-747).
   */
  private async readStorageHead(
    storagePath: string,
    byteCount: number,
    attempts = 4,
    delayMs = 300,
  ): Promise<Uint8Array | null> {
    return readStorageHead(this.ctx, storagePath, byteCount, attempts, delayMs);
  }

  /**
   * A re-upload can land on an existing row (the BAL-700 duplicate guard, or
   * the RPC's ON CONFLICT). An upload from a surface that shows its items (the
   * profile editor) then shows that row if it is private (R4). It never hides
   * one: attaching a shown photo to an application keeps it on the profile.
   * A failure is logged and the row returned as it is: the upload itself
   * succeeded, and the dancer can still switch the item on in the library.
   */
  private async showWhenUploadedShown(
    row: Database['public']['Tables']['artist_media']['Row'],
    shownOnProfile: boolean | undefined,
    logContext: Record<string, unknown>,
  ): Promise<Database['public']['Tables']['artist_media']['Row']> {
    return showWhenUploadedShown(this.ctx, row, shownOnProfile, logContext);
  }

  /**
   * Native-video → Mux ingest (BAL-721, decision D1 / R1).
   */
  private async ingestNativeVideoToMux(
    media: Database['public']['Tables']['artist_media']['Row'],
    userId: string,
    logContext: Record<string, unknown>,
  ): Promise<void> {
    return ingestNativeVideoToMux(this.ctx, media, userId, logContext);
  }

  /**
   * Add video link (YouTube, Vimeo, etc.)
   */
  async addVideoLink(
    userId: string,
    input: VideoLinkInput,
  ): Promise<Result<DancerMedia>> {
    return addVideoLink(this.ctx, userId, input);
  }

  /**
   * Get all media for a user with signed URLs for images
   */
  async getMediaByUser(userId: string): Promise<Result<DancerMedia[]>> {
    return getMediaByUser(this.ctx, userId);
  }

  /**
   * Get headshot for a user
   */
  async getHeadshot(userId: string): Promise<Result<DancerMedia | null>> {
    return getHeadshot(this.ctx, userId);
  }

  /**
   * Update media metadata (title, description, editorial category).
   *
   * `category` is the spec-vocabulary editorial bucket. Pair-validation
   * (photo vs video values) is enforced at the DB layer by
   * `artist_media_category_check`; passing an invalid pair returns a
   * `Result.fail` with the constraint message.
   */
  async updateMedia(
    mediaId: string,
    input: {
      title?: string | null;
      description?: string | null;
      category?: string | null;
    },
  ): Promise<Result<DancerMedia>> {
    return updateMedia(this.ctx, mediaId, input);
  }

  /**
   * Show or hide one item on the public profile. Hiding removes the public R2
   * copies (see dancer-media-visibility.ts); showing restores the image copy.
   * RLS decides who may touch the row: a caller who cannot read it gets
   * not found.
   */
  async setShownOnProfile(
    mediaId: string,
    shown: boolean,
  ): Promise<Result<void>> {
    return setShownOnProfile(this.ctx, mediaId, shown);
  }

  /**
   * Finish hiding a video that was made private while Mux was still fetching
   * its R2 source. `setRowShownOnProfile` keeps that source until the asset
   * leaves `preparing`; the Mux webhook calls this once it is ready or errored.
   * A shown row, or one still preparing, is left alone.
   */
  async removeDeferredPublicCopies(mediaId: string): Promise<Result<void>> {
    return removeDeferredPublicCopies(this.ctx, mediaId);
  }

  /**
   * Delete media from storage and database
   */
  async deleteMedia(mediaId: string, userId: string): Promise<Result<void>> {
    return deleteMedia(this.ctx, mediaId, userId);
  }

  /**
   * Get a signed URL for viewing/downloading media
   */
  async getSignedUrl(
    storagePath: string,
    expiresIn: number = SignedUrlExpiry.IMMEDIATE_DISPLAY,
  ): Promise<Result<string>> {
    return getSignedUrl(this.ctx, storagePath, expiresIn);
  }

  /**
   * Batch-sign multiple dancer-media storage paths in ONE round-trip.
   */
  async getSignedUrls(
    storagePaths: string[],
    expiresIn: number = SignedUrlExpiry.IMMEDIATE_DISPLAY,
  ): Promise<Map<string, string>> {
    return getSignedUrls(this.ctx, storagePaths, expiresIn);
  }

  /**
   * Persist a new display order for a set of media rows owned by `userId`.
   */
  async reorder(
    userId: string,
    items: { id: string }[],
  ): Promise<Result<void>> {
    return reorder(this.ctx, userId, items);
  }
}
